import Icon from '@tiger/icons';
import { FC, isValidElement, MouseEvent, ReactNode, useState } from 'react';
import Button from 'components/base/Button';
import Modal, { ModalConfigType } from 'components/base/Modal';
import classlist from 'utils/classlist';
import './style.scss';

export interface DialogProps extends Pick<ModalConfigType, 'size' | 'centered'> {
    title?: ReactNode;
    content: ReactNode;
    type: 'alert' | 'confirm';
    onOk?(event: MouseEvent): Promise<void> | void;
    onCancel?(event: MouseEvent): Promise<void> | void;
    btnOk?: string;
    btnCancel?: string;
    className?: string;
    /** 是否允许手动点击右上角关闭，仅允许确认的流程可以通过此参数控制 */
    hideCloseBtn?: boolean;
}

interface DialogComponent extends FC<DialogProps> {
    alert: ReturnType<typeof createDialog>;
    confirm: ReturnType<typeof createDialog>;
    open: typeof Modal.open;
}

/**
 * @description
 * 基于Modal组件，提供了Dialog.alert 和 Dialog.confirm 两个对话框方法，均已兼容PC和移动端
 *
 * onOk和onCancel作为参数传递时，如果返回值是promise，则会在promise被resolve后才关闭对话框
 */
const Dialog: DialogComponent = props => {
    const { title, content, type, className, size, btnOk = __('确定'), btnCancel = __('Cancel'), hideCloseBtn } = props;
    const [okLoading, setOkLoading] = useState<boolean>(false);
    const [cancelLoading, setCancelLoading] = useState<boolean>(false);

    const handleBtnClick = (prop: keyof Pick<DialogProps, 'onOk' | 'onCancel'>, ev: MouseEvent) => {
        const callback = props[prop];

        if (typeof callback === 'function') {
            const result = callback(ev);
            const isOnOkProp = prop === 'onOk';

            if (result && typeof result.then === 'function') {
                if (isOnOkProp) {
                    setOkLoading(true);
                } else {
                    setCancelLoading(true);
                }

                const stop = () => {
                    if (isOnOkProp) {
                        setOkLoading(false);
                    } else {
                        setCancelLoading(false);
                    }
                };

                result.then(stop, stop);
            }
        }
    };

    return (
        <div
            className={classlist('dialog-comp-root', className, {
                'dialog-comp-sm': size === 'sm',
                'dialog-comp-lg': size === 'lg'
            })}>
            {title && (
                <Modal.Header>
                    {typeof title === 'string' ? (
                        <Modal.Title className="dialog-default-header">{title}</Modal.Title>
                    ) : (
                        title
                    )}
                    {!hideCloseBtn && (
                        <Icon className="fs-5" type="close" onClick={ev => handleBtnClick('onCancel', ev)} />
                    )}
                </Modal.Header>
            )}
            {content && <Modal.Body>{content}</Modal.Body>}
            <Modal.Footer>
                {type === 'confirm' && (
                    <Button
                        type="dark"
                        ghost
                        loading={cancelLoading}
                        disabled={okLoading || cancelLoading}
                        onClick={ev => handleBtnClick('onCancel', ev)}>
                        {btnCancel}
                    </Button>
                )}
                <Button
                    type="primary"
                    loading={okLoading}
                    disabled={okLoading || cancelLoading}
                    onClick={ev => handleBtnClick('onOk', ev)}>
                    {btnOk}
                </Button>
            </Modal.Footer>
        </div>
    );
};

Dialog.alert = createDialog('alert');
Dialog.confirm = createDialog('confirm');
Dialog.open = Modal.open;

function createDialog(type: DialogProps['type']) {
    function DialogFunction(config: Omit<DialogProps, 'type'>): Promise<any>;
    function DialogFunction(config: ReactNode, title?: ReactNode): Promise<any>;
    function DialogFunction(config, title?: ReactNode): Promise<any> {
        if (isValidElement(config) || typeof config !== 'object') {
            config = {
                content: config,
                title
            };
        }

        config.type = type;

        const { onOk, onCancel, size, centered, ...restConfig } = config as DialogProps;

        return Modal.openSlideModal({
            backdrop: 'static',
            className: 'dialog-modal-root',
            responsive: true,
            keyboard: false,
            size,
            centered,
            component: ({ close, dismiss }) => (
                <Dialog
                    {...restConfig}
                    size={size}
                    onOk={ev => {
                        let result;

                        if (typeof onOk === 'function') {
                            result = onOk(ev);

                            if (result && typeof result.then === 'function') {
                                return result.then(close);
                            }
                        }

                        close(result);
                    }}
                    onCancel={ev => {
                        let result;

                        if (typeof onCancel === 'function') {
                            result = onCancel(ev);

                            if (result && typeof result.then === 'function') {
                                return result.then(dismiss);
                            }
                        }

                        dismiss(result);
                    }}
                />
            )
        }).result;
    }

    return DialogFunction;
}

export default Dialog;
